import { Popover } from "antd";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import TableLoader from "@/Components/common/TableLoader";
import No_ShiftListingIcon from "@/Components/Icons/NoData/No_ShiftListingIcon";
import { routingConstants } from "@/Constants/routingConstants";
import { displayRowPopUp, fetchShiftListing } from "@/reducers/clientdashboard";
import { getColumnNormalHtml } from "@/utilities/datatable/columnFunctions";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";
import { getComplianceClass } from "@/utilities/utils";

import BlurredText from "../../managedService/BlurredText";
import ContractInfoPopup from "../ContractInfoPopup";

/**
 * ShiftListingTable component renders a table of shift listings.
 *
 * @return {JSX.Element} The ShiftListingTable component
 */
export const ShiftListingTable = () => {
  const { shiftListing, shiftListingLoading, pagination } = useSelector(
    (state) => state.clientDashboard,
  );
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const { clientUserAllPermissions } = useSelector((state) => state.session);

  useEffect(() => {
    setData(shiftListing);
  }, [shiftListing]);
  /**
   * Returns a formatted contract date.
   *
   * @param {type} date - the input date
   * @return {type} the formatted contract date
   */
  const getContractFormattedDates = (date) => {
    const contract_date_formatted =
      moment(date).format("ddd DD") + " <br/> " + moment(date).format("MMM");
    return contract_date_formatted;
  };
  /**
   * A function that takes a time parameter and returns a string representing the break time.
   *
   * @param {string} time - The time parameter in "HH:mm:ss" format
   * @return {string} The string representing the break time
   */
  const getBreakTimeSTR = (time) => {
    const breakTime = time ? moment(time, "HH:mm:ss").format("mm") : "00";
    let breakTimeSTR = "";
    if (breakTime === "00") {
      breakTimeSTR = "No Break";
    } else {
      breakTimeSTR = `${breakTime} min break`;
    }
    return breakTimeSTR;
  };
  const columns = [
    {
      name: "Date",
      sortable: false,
      // sortField: "contract_date",
      // sortFunction: (rowA, rowB) =>
      //   sortByDate(
      //     moment(rowA.contract_date).format("DD/MM/YYYY"),
      //     moment(rowB.contract_date).format("DD/MM/YYYY"),
      //   ),
      selector: (row) => (
        <div className="flex max-w-full flex-col">
          <span className="text-[14px] font-medium leading-[normal]">
            {moment(row.contract_date).format("DD/MM/YYYY")}
          </span>

          {(!clientUserAllPermissions ||
            clientUserAllPermissions.is_allow_to_view_contract_detail === 1) &&
          (row.is_past_booking || row.contract_status == "Published") ? (
            <>
              <Link
                to={routingConstants.contractDetail(row.route_contract_id)}
                className="line-clamp-1 text-[12px] font-medium leading-[normal] text-txtColor"
              >
                {row.main_contract_ref_no}
              </Link>
            </>
          ) : (
            <span className="line-clamp-1 cursor-default text-[12px] font-medium leading-[normal] text-txtColor">
              {row.main_contract_ref_no}
            </span>
          )}
        </div>
      ),
      width: "14%",
    },
    {
      name: "Candidate",
      sortable: false,
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "candidate_name"),
      // sortField: 'candidate_forename',
      /**
       * Renders a cell with contract information and a link to candidate details.
       *
       * @param {object} row - the data for the contract
       * @return {JSX.Element} a div element containing contract information and a link to candidate details
       */
      selector: (row) => (
        <div className="flex max-w-full flex-col">
          <Popover
            trigger={"click"}
            open={row.showpopup}
            content={
              <ContractInfoPopup
                data={{
                  contract_ref_no: row.main_contract_ref_no,
                  contract_date_formatted: getContractFormattedDates(
                    row.contract_date,
                  ),
                  contract_duration: row.contract_duration,
                  candidate_name: row.candidate_name,
                  profile_image: row.profile_image,
                  role_name: row.role_name,
                  licence_no: row.licence_no,
                  profile_percent: row.profile_percent,
                  contract_start_time: row.contract_start_time,
                  contract_end_time: row.contract_end_time,
                  mobile_phone_number: row.mobile_phone_number,
                  candidate_email: row.candidate_email,
                  breakTimeSTR: getBreakTimeSTR(row.break_time),
                  route_id: row.route_id, // candidate detail route
                  route_contract_id: row.route_contract_id, // contract detail route
                  candidate_id: row.candidate_id,
                }}
              />
            }
          >
            <div className="overflow-hidden">
              {(!clientUserAllPermissions ||
                clientUserAllPermissions.is_allow_to_view_candidate_profile ===
                  1) &&
              (row.is_past_booking || row.contract_status == "Published") ? (
                row.route_id ? (
                  <Link
                    to={routingConstants.candidateDetail(row.route_id)}
                    className="truncate text-[14px] font-medium leading-[normal]"
                  >
                    {row.candidate_name}
                  </Link>
                ) : (
                  getColumnNormalHtml(row.candidate_name, "truncate")
                )
              ) : (
                <span className="cursor-default truncate text-[14px] font-medium leading-[normal]">
                  {row.candidate_name}
                </span>
              )}
              {/* {row.route_id ? (
                <Link
                  to={routingConstants.candidateDetail(row.route_id)}
                  className="truncate text-[14px] font-medium leading-[normal]"
                >
                  {row.candidate_name}
                </Link>
              ) : (
                getColumnNormalHtml(row.candidate_name, "truncate")
              )} */}
              <BlurredText
                type="phone"
                blurKey={
                  row.is_past_booking || row.contract_status == "Published"
                }
              >
                <span className="block text-[12px] font-medium leading-[normal] text-txtColor">
                  {row.mobile_phone_number}
                </span>
              </BlurredText>
            </div>
          </Popover>
        </div>
      ),
      width: "19%",
    },
    {
      name: "Compliance",
      sortable: false,
      // sortField: "profile_percent",
      /**
       * Renders a cell with a percentage value and styling based on the row data.
       *
       * @param {object} row - The data for the row
       * @return {JSX.Element} A React component representing the cell
       */
      selector: (row) => (
        <div className="flex flex-col">
          <span
            className={`h-[23px] w-[55px] rounded-[4px] border text-center text-[14px] font-medium leading-[23px] ${getComplianceClass(
              row.profile_percent,
            )}`}
          >
            {row.profile_percent == "" || row.profile_percent == "-"
              ? "0%"
              : row.profile_percent + "%"}
          </span>
        </div>
      ),
      width: "12%",
    },
    {
      name: "Role",
      sortable: false,
      // sortFunction: (rowA, rowB) => sortByText(rowA.role_name, rowB.role_name),
      // sortField: 'role_name',
      /**
       * A function that renders a cell for a given row.
       *
       * @param {type} row - the row object
       * @return {type} the rendered cell
       */
      selector: (row) => (
        <div className="flex flex-col">
          {getColumnNormalHtml(
            row.role_name,
            "line-clamp-2 whitespace-break-spaces",
          )}
          <span className="text-[12px] font-medium leading-[normal] text-txtColor">
            {row.licence_no}
          </span>
        </div>
      ),
      width: "13%",
    },
    {
      name: "Start/End",
      sortable: false,
      // sortField: "contract_start_time",
      // sortFunction: (rowA, rowB) =>
      // sortByTime(
      //   moment(rowA.contract_start_time, "HH:mm:ss").format("HH:mm"),
      //   moment(rowB.contract_start_time, "HH:mm:ss").format("HH:mm"),
      // ),
      selector: (row) => (
        <div className="flex w-full flex-col items-end gap-1">
          {getColumnNormalHtml(
            moment(row.contract_start_time, "HH:mm:ss").format("HH:mm"),
          )}
          <span className="leading-[0px]">-</span>
          {getColumnNormalHtml(
            moment(row.contract_end_time, "HH:mm:ss").format("HH:mm"),
          )}
        </div>
      ),
      width: "8%",
    },
    {
      name: "Break",
      sortable: false,
      // sortField: 'un_paid_break_hour',
      // sortFunction: (rowA, rowB) =>
      //   sortByTime(
      //     moment(rowA.break_time, "HH:mm:ss").format("HH:mm"),
      //     moment(rowB.break_time, "HH:mm:ss").format("HH:mm"),
      //   ),
      /**
       * A function that takes a row and returns a div element containing a span with text based on the value of row.break_time.
       *
       * @param {Object} row - The row object
       * @return {JSX.Element} The div element containing the span
       */
      selector: (row) => (
        <div className="flex flex-col">
          {getColumnNormalHtml(
            row.break_time
              ? moment(row.break_time, "HH:mm:ss").format("HH:mm")
              : "00:00",
          )}
        </div>
      ),
      width: "8%",
    },
    {
      name: "Admin",
      sortable: false,
      // sortField: "contract_admin_time",
      // sortFunction: (rowA, rowB) =>
      //   sortByTime(
      //     moment(rowA.admin_time, "HH:mm:ss").format("HH:mm"),
      //     moment(rowB.admin_time, "HH:mm:ss").format("HH:mm"),
      //   ),
      /**
       * A function that takes a row as input and returns a JSX element.
       *
       * @param {Object} row - the input row
       * @return {JSX.Element} a JSX element
       */
      selector: (row) => (
        <div className="flex flex-col">
          {getColumnNormalHtml(
            moment(row.admin_time, "HH:mm:ss").format("HH:mm"),
          )}
        </div>
      ),
      width: "8%",
    },
    {
      name: "Visit",
      sortable: false,
      // sortField: "contract_no_of_visit",
      // sortFunction: (rowA, rowB) =>
      //   sortByNumber(rowA, rowB, "contract_no_of_visit"),
      /**
       * A function that renders a cell with the given row data.
       *
       * @param {object} row - The row data to render
       * @return {JSX.Element} The rendered cell component
       */
      selector: (row) => (
        <div className="flex flex-col">
          {getColumnNormalHtml(row.contract_no_of_visit)}
        </div>
      ),
      width: "8%",
    },
    {
      name: "Total Hours",
      /**
       * A function that renders a cell in a specific format.
       *
       * @param {object} row - the data for the cell
       * @return {JSX.Element} the rendered cell component
       */
      selector: (row) => (
        <div className="flex flex-col">
          {getColumnNormalHtml(row.contract_duration)}
        </div>
      ),
      width: "10%",
    },
  ];
  const dataTableRef = useRef(null);
  /**
   * Function to handle table scroll event.
   */

  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchShiftListing()),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  const conditionalRowStyles = [
    {
      when: (row) =>
        row.contract_status !== "Published" &&
        row.contract_status !== "Applied" &&
        row.route_id !== null,
      style: {
        cursor: "pointer",
      },
    },
    {
      when: (row) =>
        row.contract_status === "Published" ||
        row.contract_status === "Applied" ||
        row.route_id === null,
      style: {
        cursor: "default",
      },
    },
  ];

  return (
    <div
      className={`ShiftListingTable_Wrapper DataTable_Wrapper flex-1 cursor-pointer pt-4`}
      ref={dataTableRef}
    >
      <DataTable
        columns={columns}
        data={data}
        noDataComponent={
          <div className="pt-[73px]">
            <No_ShiftListingIcon />
            <span className="text-[18px] font-semibold leading-[normal] text-primary">
              No Upcoming Shift
            </span>
          </div>
        }
        fixedHeader
        fixedHeaderScrollHeight={"420px"}
        // persistTableHead={true}
        progressPending={shiftListingLoading}
        conditionalRowStyles={conditionalRowStyles}
        progressComponent={<TableLoader />}
        className="_DataTable"
        onRowClicked={(row) => {
          const { row_id, contract_status, route_id, showpopup } = row;
          if (
            contract_status !== "Published" &&
            contract_status !== "Applied" &&
            route_id !== null
          ) {
            dispatch(
              displayRowPopUp({
                rowID: row_id,
                show: !showpopup,
              }),
            );
          }
        }}
        onRowMouseLeave={(row) => {
          const { row_id, contract_status, route_id } = row;
          if (
            contract_status !== "Published" &&
            contract_status !== "Applied" &&
            route_id !== null
          ) {
            dispatch(
              displayRowPopUp({
                rowID: row_id,
                show: false,
              }),
            );
          }
        }}
      />
    </div>
  );
};
